import { NoteAdd as NoteAddIcon } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import React, { useCallback } from "react";
import { Accept, DropEvent, FileRejection, useDropzone } from "react-dropzone";

import { Colors } from "../../common/colors";
import { CenteredSpinner } from "../spinner/CenteredSpinner";

interface IProps {
    acceptedFileTypes?: Accept;
    loading?: boolean;
    minimal?: boolean;
    maxFiles?: number;
    maxSize?: number;
    objectName?: string;
    onDrop?(acceptedFiles: File[], fileRejections: FileRejection[]): void;
    onDropAccepted?(files: File[]): void;
}

export const FileDragAndDropInput: React.FC<IProps> = ({
    acceptedFileTypes,
    loading,
    minimal,
    maxFiles,
    maxSize,
    objectName = "file",
    onDrop,
    onDropAccepted,
}) => {
    const handleDropAccepted = useCallback(
        (files: File[], _event: DropEvent): void => onDropAccepted?.(files),
        [onDropAccepted],
    );
    const { getRootProps, getInputProps, isDragActive, fileRejections, open } = useDropzone({
        accept: acceptedFileTypes,
        noClick: true,
        maxFiles,
        onDropAccepted: handleDropAccepted,
        onDrop,
        maxSize,
        disabled: loading,
    });

    function renderButton() {
        return (
            <Button
                variant="outlined"
                size="small"
                onClick={open}
                sx={({
                    palette: {
                        common: { white },
                    },
                }) => ({ bgcolor: white })}
            >
                Browse Files
            </Button>
        );
    }

    function renderContent() {
        if (loading) {
            return <CenteredSpinner />;
        }

        if (minimal) {
            return (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography>Drag and drop your {objectName} here or</Typography>
                    {renderButton()}
                </Stack>
            );
        }

        return (
            <Box p={5}>
                <Stack spacing={5} alignItems="center">
                    <NoteAddIcon htmlColor={Colors.Blue[300]} />
                    <Stack spacing={1} alignItems="center">
                        <Typography>Drag and drop your {objectName} to get started</Typography>
                        <Typography color="text.secondary">or</Typography>
                        {renderButton()}
                    </Stack>
                </Stack>
            </Box>
        );
    }

    function maybeRenderErrors() {
        if (fileRejections.length === 0) {
            return null;
        }

        return (
            <Stack spacing={1}>
                {fileRejections.map(({ file, errors }, index) => (
                    <Typography key={index} color="error.main">
                        {file.name}: {errors.map(({ message }) => message).join(". ")}
                    </Typography>
                ))}
            </Stack>
        );
    }

    return (
        <Stack spacing={1}>
            <Box
                {...getRootProps()}
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
                p={0}
                sx={{
                    background: isDragActive ? Colors.Blue[50] : Colors.Gray[50],
                    border: 1,
                    borderColor: isDragActive ? Colors.Blue[300] : Colors.Gray[200],
                    borderRadius: 1,
                }}
            >
                <input {...getInputProps()} />
                {renderContent()}
            </Box>

            {maybeRenderErrors()}
        </Stack>
    );
};
